import { useMemo } from "react";
import {
  useFilterCompanies,
  useFilterJobs,
  useUserContext,
} from "@/providers/contexts";
import { useCompanyFilterOptions, useJobPostFilterOptions } from "@/hooks";
import { Autocomplete } from "@/components/atoms";
import { LocationAutocompleteOption } from "@/components/atoms/Autocomplete";
import { FilterPanelProps } from "../FilterPanel";

export interface LocationFilterProps {
  page: FilterPanelProps["page"];
}

export const LocationFilter = ({ page }: LocationFilterProps) => {
  const { disabledBasicContent, disabledProContent, isAuthenticated } =
    useUserContext();
  const { locations: companyLocations } = useCompanyFilterOptions();
  const { filters: jobPostFilterOptions } = useJobPostFilterOptions();
  const {
    filters: jobFilters,
    dispatch: jobDispatch,
    loading: jobLoading,
  } = useFilterJobs();
  const {
    filters: companyFilters,
    dispatch: companyDispatch,
    loading: companyLoading,
  } = useFilterCompanies();

  const isSearchPage = page === "search";
  const loading = isSearchPage ? jobLoading : companyLoading;

  const locations = isSearchPage
    ? jobPostFilterOptions?.locations || []
    : companyLocations;
  const { selectedLocations } = isSearchPage ? jobFilters : companyFilters;
  const dispatch = isSearchPage ? jobDispatch : companyDispatch;

  const disabledContent =
    disabledBasicContent || disabledProContent || !isAuthenticated;

  const locationOptions: LocationAutocompleteOption[] = useMemo(
    () =>
      locations
        ?.filter(location => location.location_type !== "other")
        .map(location => ({
          key: location.id.toString(),
          value: location.name,
          display: location.parent_name
            ? `${location.name} (${location.parent_name})`
            : location.name,
          location_type: location.location_type,
          is_active: location.is_active ?? true,
        })),
    [locations],
  );

  const value: LocationAutocompleteOption[] = useMemo(() => {
    return selectedLocations.map(selected => {
      const matched = locationOptions.find(loc => loc.value === selected.name);
      return (
        matched || {
          key: selected.id.toString(),
          value: selected.name,
          display: selected.name,
          location_type: selected.location_type || "unknown",
          is_active: true,
        }
      );
    });
  }, [selectedLocations, locationOptions]);

  return (
    <Autocomplete
      multiple
      componentName="location-filter"
      inputLabel="Location"
      options={locationOptions}
      groupBy={option => (option as LocationAutocompleteOption).location_type}
      getOptionDisabled={option =>
        !(option as LocationAutocompleteOption).is_active
      }
      value={value}
      onChange={(_, newValue) => {
        if (newValue && (newValue as LocationAutocompleteOption[]).length > 0) {
          const validLocations = (
            newValue as LocationAutocompleteOption[]
          ).filter(
            location => location.value !== "No matching job post locations",
          );
          const selected = locations.filter(location =>
            validLocations.some(el => el.value === location.name),
          );
          dispatch({
            type: "SET_FILTERS",
            payload: { selectedLocations: selected },
          });
        } else {
          dispatch({ type: "SET_FILTERS", payload: { selectedLocations: [] } });
        }
      }}
      loading={loading}
      tooltipMessage={
        disabledContent
          ? "Upgrade your account to access this feature"
          : undefined
      }
      readMoreLink="/memberships"
    />
  );
};
